import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, RefreshCw, X, ZapOff } from "lucide-react";
import { useLanguage } from "../i18n";

const BLUR_THRESHOLD = 55;

export function canvasBlurScore(canvas) {
  const size = 128;
  const small = document.createElement("canvas");
  small.width = size;
  small.height = size;
  const ctx = small.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size).data;
  const gray = new Float32Array(size * size);
  for (let i = 0; i < gray.length; i++) {
    const p = i * 4;
    gray[i] = data[p] * 0.299 + data[p + 1] * 0.587 + data[p + 2] * 0.114;
  }
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const i = y * size + x;
      const lap = gray[i - 1] + gray[i + 1] + gray[i - size] + gray[i + size] - 4 * gray[i];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }
  const mean = sum / count;
  return Math.max(0, sumSq / count - mean * mean);
}

export async function imageFileBlurScore(file) {
  if (!file.type.startsWith("image/")) return null;
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvasBlurScore(canvas);
}

export function CameraCapture({ kind, title, onCapture, onClose }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);
  const [blurry, setBlurry] = useState(null);
  const [liveSharp, setLiveSharp] = useState(null);
  const isSelfie = kind.startsWith("selfie");

  useEffect(() => {
    let alive = true;
    navigator.mediaDevices?.getUserMedia({
      video: { facingMode: isSelfie ? "user" : "environment", width: { ideal: 1280 }, height: { ideal: 960 } },
      audio: false,
    }).then((stream) => {
      if (!alive) { stream.getTracks().forEach((track) => track.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setReady(true);
    }).catch(() => setError(true));
    return () => {
      alive = false;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [isSelfie]);

  // Analisi live del frame: ogni 600ms misura la nitidezza del video e guida
  // l'utente prima dello scatto (badge + colore della cornice)
  useEffect(() => {
    if (!ready || error) return undefined;
    const probe = document.createElement("canvas");
    const tick = () => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      probe.width = video.videoWidth;
      probe.height = video.videoHeight;
      probe.getContext("2d", { willReadFrequently: true }).drawImage(video, 0, 0);
      setLiveSharp(canvasBlurScore(probe) >= BLUR_THRESHOLD);
    };
    const timer = setInterval(tick, 600);
    return () => clearInterval(timer);
  }, [ready, error]);

  const finish = (blob, score, skipWarning = false) => {
    if (!blob) return;
    const file = new File([blob], `${kind}-${Date.now()}.jpg`, { type: "image/jpeg" });
    onCapture(file, { skipBlur: skipWarning, blurScore: score });
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setChecking(true);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const score = canvasBlurScore(canvas);
    canvas.toBlob((blob) => {
      setChecking(false);
      if (score < BLUR_THRESHOLD) setBlurry({ blob, score });
      else finish(blob, score, true);
    }, "image/jpeg", 0.92);
  };

  return (
    <div data-testid="guided-camera-modal" className="fixed inset-0 z-[400] bg-[#0F172A] text-white flex flex-col">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">{title}</div>
        <button type="button" data-testid="guided-camera-close" onClick={onClose} aria-label={t("auto.close")}
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <video ref={videoRef} data-testid="guided-camera-video" autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/20" aria-hidden="true" />
        <div data-testid="guided-camera-frame" aria-hidden="true"
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border-4 shadow-[0_0_0_999px_rgba(15,23,42,0.45)] transition-colors duration-300 ${
            liveSharp === null ? "border-[#FF6B35]" : liveSharp ? "border-emerald-400" : "border-amber-400"} ${
            isSelfie ? "w-56 h-72 rounded-[50%]" : "w-[82%] max-w-sm aspect-[1.586] rounded-3xl"}`}>
          {isSelfie && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-white/50" />}
        </div>
        {ready && !error && liveSharp !== null && (
          <div data-testid="live-focus-badge"
            className={`absolute left-1/2 top-4 -translate-x-1/2 rounded-full px-4 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-lg transition-colors duration-300 ${
              liveSharp ? "bg-emerald-500/90" : "bg-amber-500/90 animate-pulse"}`}>
            {liveSharp ? t("id.focusOk") : t("id.focusChecking")}
          </div>
        )}
        {!ready && !error && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        {error && (
          <div data-testid="guided-camera-error" className="absolute inset-x-6 top-1/3 rounded-3xl bg-white text-slate-800 p-5 text-center">
            <ZapOff className="w-8 h-8 mx-auto text-[#FF6B35]" />
            <p className="mt-3 text-sm">{t("id.cameraUnavailable")}</p>
          </div>
        )}
      </div>
      <div className="px-6 pb-8 pt-4 text-center safe-area-bottom">
        <p className="text-sm text-slate-300">{isSelfie ? t("id.stepSelfie") : t("id.stepDoc")}</p>
        <button type="button" data-testid="guided-capture-button" onClick={capture} disabled={!ready || checking}
          className="mt-4 w-20 h-20 rounded-full border-4 border-white bg-[#FF6B35] mx-auto flex items-center justify-center shadow-xl disabled:opacity-50">
          {checking ? <Loader2 className="w-7 h-7 animate-spin" /> : <Camera className="w-8 h-8" />}
        </button>
      </div>
      {blurry && (
        <div data-testid="blur-warning" className="absolute inset-x-4 bottom-28 rounded-3xl bg-white text-slate-900 p-5 shadow-2xl">
          <div className="font-display font-bold text-lg">{t("id.blurryTitle")}</div>
          <p className="mt-1 text-sm text-slate-500">{t("id.blurryBody")}</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button type="button" data-testid="blur-retake-button" onClick={() => setBlurry(null)}
              className="rounded-full border border-slate-200 px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest">
              <RefreshCw className="inline w-3.5 h-3.5 mr-1" /> {t("id.retake")}
            </button>
            <button type="button" data-testid="blur-use-anyway-button" onClick={() => finish(blurry.blob, blurry.score, true)}
              className="rounded-full bg-[#1E293B] text-white px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest">
              <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" /> {t("id.useAnyway")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
